import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Paper,
  LinearProgress,
  Grid
} from '@mui/material';
import { Upload as UploadIcon, Info as InfoIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid, getISOWeek, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
import { useTeam } from '../../context/TeamContext';

const normalizeStr = str =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// Reconnaît une ligne astreinte quelle que soit la casse / diacritiques
const isAstreinteRow = (val) => normalizeStr(val).includes('astreinte');

const LiisaOnCallImport = ({ onSuccess }) => {
  const [openDialog, setOpenDialog]   = useState(false);
  const [error, setError]             = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress]       = useState({ percent: 0, label: '' });
  const [dateFrom, setDateFrom]       = useState(null);
  const [dateTo, setDateTo]           = useState(null);
  const fileInputRef = useRef();

  const { technicians }         = useTechnicians();
  const { syncLiisaOnCalls }    = useOnCalls();
  const { selectedTeam }        = useTeam();

  const extractNameFromLiisaTechnician = (technicianField) => {
    if (!technicianField) return null;
    let cleanName = technicianField
      .replace(/\s+(UI|DTSI|CERAC|DIR|DORM|DONC|DOCE|DOE|DOSO|DONDF|DOIDF|DOSE|REUNION|MAYOTTE|NDF|OC|AFFAIRES)\s+.*$/i, '')
      .replace(/\s+UI\s+\w+/i, '')
      .trim();
    const parts = cleanName.split(' ');
    if (parts.length >= 2) {
      return { lastName: parts[0].trim(), firstName: parts.slice(1).join(' ').trim() };
    }
    return null;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setError('');
    setProgress({ percent: 0, label: 'Lecture du fichier…' });

    try {
      // Détection encodage UTF-8 / ISO-8859-1
      const readFileAsText = (f, encoding) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(e);
        reader.readAsText(f, encoding);
      });

      let text = await readFileAsText(file, 'UTF-8');
      if (text.split('\n')[0].includes('\uFFFD')) {
        text = await readFileAsText(file, 'ISO-8859-1');
      }

      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      if (lines.length < 2) {
        throw new Error('Le fichier doit contenir au moins une ligne d\'en-tête et une ligne de données');
      }

      const headerColumns = lines[0].split(';').map(c => c.trim());
      const findCol = (predicate) => headerColumns.findIndex(c => predicate(normalizeStr(c)));

      const dateDebutIndex  = findCol(n => n.includes('date') && n.includes('debut'));
      const dateFinIndex    = findCol(n => n.includes('date') && n.includes('fin') && !n.includes('export'));
      const technicienIndex = findCol(n => n.includes('technicien') || n.includes('effectif'));
      // Colonne "Réservation" dans VP Astreintes (ou "Indisponibilité" / "Absence" dans d'autres exports)
      const typeIndex       = findCol(n =>
        n.includes('reservation') || n.includes('indisponibilit') || n.includes('absence')
      );

      if (dateDebutIndex < 0 || dateFinIndex < 0 || technicienIndex < 0 || typeIndex < 0) {
        throw new Error(
          `Format de fichier non reconnu.\n` +
          `En-têtes trouvés : ${headerColumns.join('; ')}\n` +
          `Colonnes requises : Date de début, Date de fin, Effectifs (ou Technicien), Réservation (ou Indisponibilité)`
        );
      }

      // Filtre de plage de dates sélectionnée par l'utilisateur
      const filterStart = dateFrom ? startOfDay(dateFrom) : null;
      const filterEnd   = dateTo   ? endOfDay(dateTo)     : null;

      // Techniciens de l'équipe sélectionnée
      const teamTechnicians = selectedTeam
        ? technicians.filter(t => t.teamId === selectedTeam.id)
        : technicians;

      const techByLastName = new Map();
      for (const t of teamTechnicians) {
        const key = t.lastName.toLowerCase();
        if (!techByLastName.has(key)) techByLastName.set(key, []);
        techByLastName.get(key).push(t);
      }
      const knownLastNames = new Set(techByLastName.keys());

      const onCalls = [];
      const errors  = [];
      const seen    = new Set();
      let astreinteRowsFound = 0;

      setProgress({ percent: 20, label: 'Analyse des lignes…' });

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(';').map(c => c.trim());
        if (cols.length < headerColumns.length) continue;

        const typeVal = cols[typeIndex] || '';
        if (!isAstreinteRow(typeVal)) continue;
        astreinteRowsFound++;

        try {
          const dateDebut       = cols[dateDebutIndex];
          const dateFin         = cols[dateFinIndex];
          const technicianField = cols[technicienIndex];

          // Parser les dates
          let startDate, endDate, startDateObj, endDateObj;
          try {
            startDateObj = parse(dateDebut.split(' ')[0], 'dd/MM/yyyy', new Date());
            endDateObj   = parse(dateFin.split(' ')[0],   'dd/MM/yyyy', new Date());
            if (!isValid(startDateObj) || !isValid(endDateObj)) throw new Error('Date invalide');
            startDate = format(startDateObj, 'yyyy-MM-dd');
            endDate   = format(endDateObj,   'yyyy-MM-dd');
          } catch {
            errors.push(`Ligne ${i + 1}: Date invalide pour ${technicianField}`);
            continue;
          }

          // Appliquer le filtre de plage de dates
          if (filterStart && startDateObj < filterStart) continue;
          if (filterEnd   && startDateObj > filterEnd)   continue;

          const nameInfo = extractNameFromLiisaTechnician(technicianField);
          if (!nameInfo) continue;

          const lastNameKey = nameInfo.lastName.toLowerCase();
          if (!knownLastNames.has(lastNameKey)) continue;

          const candidates = techByLastName.get(lastNameKey);
          const technician = candidates.find(t =>
            t.firstName.toLowerCase().includes(nameInfo.firstName.toLowerCase().split(' ')[0])
          );
          if (!technician) continue;

          // Dédoublonnage
          const key = `${technician.id}|${startDate}`;
          if (seen.has(key)) continue;
          seen.add(key);

          // Récupérer les IDs de compétences du technicien pour qu'il apparaisse dans la vue Planning
          const techSkillIds = (technician.skills || []).map(s =>
            typeof s === 'object' && s.skillId ? s.skillId : s
          );

          onCalls.push({
            technicianId: technician.id,
            startDate,
            endDate,
            weekNumber: getISOWeek(startDateObj),
            skills: techSkillIds,
            createdBy: 'import-liisa',
            comment: `Import Visual Planning - ${typeVal}`
          });

        } catch (err) {
          errors.push(`Ligne ${i + 1}: ${err.message}`);
        }
      }

      if (astreinteRowsFound === 0) {
        const sampleValues = [...new Set(
          lines.slice(1, 30)
            .map(l => l.split(';')[typeIndex]?.trim())
            .filter(Boolean)
        )].slice(0, 6).join('", "');
        throw new Error(
          `Aucune ligne "astreinte" trouvée dans le fichier.\n` +
          `Colonne analysée : "${headerColumns[typeIndex]}" (index ${typeIndex})\n` +
          `Valeurs trouvées dans les premières lignes : "${sampleValues}"\n` +
          `La valeur doit contenir "astreinte" (ex: "00 - Astreinte").`
        );
      }

      if (onCalls.length === 0) {
        throw new Error(
          `Aucune astreinte valide trouvée pour l'équipe "${selectedTeam?.name || 'sélectionnée'}"` +
          (filterStart || filterEnd ? ` dans la plage sélectionnée` : '') +
          `.\n${astreinteRowsFound} ligne(s) "astreinte" dans le fichier mais aucun technicien ne correspond.`
        );
      }

      if (errors.length > lines.length / 2) {
        throw new Error('Trop d\'erreurs :\n' + errors.slice(0, 10).join('\n'));
      }

      // Synchronisation
      const { created, deleted, unchanged } = await syncLiisaOnCalls(
        onCalls,
        ({ percent, label }) => setProgress({ percent, label }),
        filterStart,
        filterEnd
      );

      setOpenDialog(false);
      setError('');

      const parts = ['✅ Synchronisation astreintes Liisa terminée !'];
      if (created   > 0) parts.push(`${created} astreinte(s) créée(s)`);
      if (deleted   > 0) parts.push(`${deleted} astreinte(s) supprimée(s) (disparues du fichier)`);
      if (unchanged > 0) parts.push(`${unchanged} astreinte(s) inchangée(s)`);
      if (errors.length > 0) parts.push(`${errors.length} ligne(s) ignorée(s)`);
      alert(parts.join('\n'));

      if (onSuccess) onSuccess();
      event.target.value = '';

    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Box>
        <Tooltip title="Importer des astreintes depuis Visual Planning (Liisa)">
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ mr: 1 }}
          >
            Import Astreintes Liisa
          </Button>
        </Tooltip>

        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <Dialog open={openDialog} onClose={() => !isProcessing && setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Import d'astreintes depuis Visual Planning (Liisa)
            <Tooltip title="Exporte le fichier CSV depuis Visual Planning (toutes activités). Seules les lignes avec Réservation = '00 - Astreinte' seront importées.">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>

          <DialogContent>
            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="body2">
                <strong>Instructions :</strong>
                <br />• Exportez le fichier CSV depuis Visual Planning (toutes activités confondues)
                <br />• Seules les lignes dont la colonne <em>Réservation</em> contient <strong>"00 - Astreinte"</strong> seront importées
                <br />• Seuls les techniciens de l'équipe "{selectedTeam?.name || 'sélectionnée'}" seront pris en compte
                <br />• Les astreintes déjà présentes dans la période seront synchronisées (ajout / suppression)
              </Typography>
            </Paper>

            {/* Filtre de plage de dates */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Plage de dates à importer <Typography component="span" variant="caption" color="text.secondary">(optionnel — laissez vide pour tout importer)</Typography>
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <DatePicker
                  label="Du"
                  value={dateFrom}
                  onChange={setDateFrom}
                  format="dd/MM/yyyy"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Au"
                  value={dateTo}
                  onChange={setDateTo}
                  format="dd/MM/yyyy"
                  minDate={dateFrom || undefined}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                  {error}
                </Typography>
              </Alert>
            )}

            {isProcessing && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{progress.label}</Typography>
                <LinearProgress variant="determinate" value={progress.percent} />
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} disabled={isProcessing}>
              Annuler
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              {isProcessing ? 'Traitement…' : 'Choisir le fichier CSV'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default LiisaOnCallImport;
